import { tipoIntermediador, conjuge } from "../dynamicTextRegistry";
import { maskCpf, maskPhoneBr } from "../../utils/masks";
import {
  formatDecimalBrForDoc,
  formatBrlCurrencyForDoc,
} from "../../utils/brFormat";

function formatCpfPreview(value) {
  return maskCpf(value ?? "");
}

function formatPhonePreview(value) {
  return maskPhoneBr(value ?? "");
}

function formatDateBR(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString("pt-BR");
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return s;
  return parsed.toLocaleDateString("pt-BR");
}

function escapeHtml(value) {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function strongLabel(text) {
  return `<strong>${escapeHtml(text)}</strong>`;
}

function replacePlaceholdersInStringEscaped(template, values) {
  if (!template) return "";
  return template.replace(/\{(.*?)\}/g, (_, key) =>
    escapeHtml(values[key] ?? "")
  );
}

function boldConjugeTemplateLabels(html) {
  return html
    .replace(/CÔNJUGE:/g, `${strongLabel("CÔNJUGE:")}`)
    .replace(/\bCPF:/g, `${strongLabel("CPF:")}`)
    .replace(/\bRG:/g, `${strongLabel("RG:")}`)
    .replace(/NACIONALIDADE:/g, `${strongLabel("NACIONALIDADE:")}`)
    .replace(/PROFISSÃO:/g, `${strongLabel("PROFISSÃO:")}`)
    .replace(/\bEMAIL:/g, `${strongLabel("EMAIL:")}`);
}

function renderConjugeBlockHtml(person) {
  if (person?.ESTADO_CIVIL_PESSOA_FISICA !== "casado") return "";
  const tpl = conjuge.casado;
  if (!tpl) return "";
  const valuesForPreview = {
    ...person,
    CPF_CONJUGE: formatCpfPreview(person.CPF_CONJUGE),
  };
  const filled = replacePlaceholdersInStringEscaped(tpl, valuesForPreview).trim();
  if (!filled) return "";
  return boldConjugeTemplateLabels(filled);
}

function buildPessoaFisicaBlockHtml(person) {
  const parts = [
    `${strongLabel("NOME:")} ${escapeHtml(person.NOME_PESSOA_FISICA)}`,
    `${strongLabel("CPF:")} ${escapeHtml(formatCpfPreview(person.CPF_PESSOA_FISICA))}`,
    `${strongLabel("RG:")} ${escapeHtml(person.RG_PESSOA_FISICA)}`,
    `${strongLabel("NACIONALIDADE:")} ${escapeHtml(
      person.NACIONALIDADE_PESSOA_FISICA
    )}`,
    `${strongLabel("PROFISSÃO:")} ${escapeHtml(person.PROFISSAO_PESSOA_FISICA)}`,
    `${strongLabel("ESTADO CIVIL:")} ${escapeHtml(
      person.ESTADO_CIVIL_PESSOA_FISICA
    )}`,
  ];
  const conj = renderConjugeBlockHtml(person);
  if (conj) parts.push(conj);
  parts.push(
    `${strongLabel("ENDEREÇO:")} ${escapeHtml(
      person.RUA_ENDERECO_PESSOA_FISICA
    )}, ${strongLabel("nº")} ${escapeHtml(
      person.N_ENDERECO_PESSOA_FISICA
    )}, ${strongLabel("BAIRRO")} ${escapeHtml(
      person.BAIRRO_ENDERECO_PESSOA_FISICA
    )}, ${escapeHtml(person.MUN_ENDERECO_PESSOA_FISICA)} ${strongLabel(
      "- CEP:"
    )} ${escapeHtml(person.CEP_PESSOA_FISICA)}`,
    `${strongLabel("EMAIL:")} ${escapeHtml(person.EMAIL_PESSOA_FISICA)}`,
    `${strongLabel("TELEFONE:")} ${escapeHtml(
      formatPhonePreview(person.TEL_PESSOA_FISICA)
    )}`
  );
  return parts.join(" ");
}

function signatureBlock(name, cpfOrDoc) {
  return `________________________\n${name ?? ""}\n${cpfOrDoc ?? ""}`;
}

function signatureBlockConjugeHtml(name, cpf) {
  const nameEsc = escapeHtml(name ?? "");
  const cpfEsc = escapeHtml(formatCpfPreview(cpf));
  return `<div class="signature-conjuge-block space-y-1 mb-1"><div>________________________</div><div>${nameEsc}</div><div>${cpfEsc}</div></div>`;
}

const EXCLUSIVIDADE_TEXTO = {
  exclusividade: "exclusividade",
  sem_exclusividade: "sem exclusividade",
};

function buildIntermediadorPreviewHtml(formData) {
  const block = formData.intermediador;
  const type = block?.type;
  if (!type) return "";
  const template = tipoIntermediador[type];
  if (!template) return "";
  const fields = block.fields || {};

  const values = { ...fields };
  if (type === "corretor") {
    values.CPF_CORRETOR = formatCpfPreview(fields.CPF_CORRETOR);
    values.TEL_CORRETOR = formatPhonePreview(fields.TEL_CORRETOR);
  }
  if (type === "imobiliaria") {
    values.CPF_REPRESENTANTE_LEGAL = formatCpfPreview(
      fields.CPF_REPRESENTANTE_LEGAL
    );
    values.TEL_IMOBILIARIA = formatPhonePreview(fields.TEL_IMOBILIARIA);
    if (fields.INTERM_CRECI_IMOBILIARIA != null && fields.INTERM_CRECI_IMOBILIARIA !== "") {
      values.CRECI_IMOBILIARIA = fields.INTERM_CRECI_IMOBILIARIA;
    }
    if (fields.INTERM_EMAIL_IMOBILIARIA != null && fields.INTERM_EMAIL_IMOBILIARIA !== "") {
      values.EMAIL_IMOBILIARIA = fields.INTERM_EMAIL_IMOBILIARIA;
    }
  }

  const raw = replacePlaceholdersInStringEscaped(template.trim(), values);
  return `<p class="text-justify whitespace-pre-line">${raw}</p>`;
}

function buildAssinaturaIntermediador(formData) {
  const type = formData.intermediador?.type;
  const f = formData.intermediador?.fields || {};
  if (type === "corretor") {
    return signatureBlock(f.NOME_CORRETOR, formatCpfPreview(f.CPF_CORRETOR));
  }
  if (type === "imobiliaria") {
    return signatureBlock(
      f.NOME_REPRESENTANTE_LEGAL,
      formatCpfPreview(f.CPF_REPRESENTANTE_LEGAL)
    );
  }
  return "";
}

export function autorizacaoGlebaLoteamentoResolver(formData, userConfig) {
  const safeUserConfig = userConfig || {};
  const data = { ...safeUserConfig, ...formData };

  const cidadeSig =
    data.CIDADE_ASSINATURA !== undefined && data.CIDADE_ASSINATURA !== null
      ? data.CIDADE_ASSINATURA
      : data.cidade_assinatura;
  const dataSig =
    data.DATA_ASSINATURA !== undefined && data.DATA_ASSINATURA !== null
      ? data.DATA_ASSINATURA
      : data.data_assinatura;
  if (cidadeSig !== undefined && cidadeSig !== null && cidadeSig !== "") {
    data.CIDADE_ASSINATURA = cidadeSig;
  }
  if (dataSig !== undefined && dataSig !== null && dataSig !== "") {
    data.DATA_ASSINATURA = dataSig;
  }

  data.ENDERECO_IMOBILIARIA =
    formData.ENDERECO_IMOBILIARIA !== undefined &&
    formData.ENDERECO_IMOBILIARIA !== null
      ? formData.ENDERECO_IMOBILIARIA
      : safeUserConfig.endereco_imobiliaria || "";
  data.CELULAR_IMOBILIARIA = formatPhonePreview(
    formData.CELULAR_IMOBILIARIA !== undefined &&
      formData.CELULAR_IMOBILIARIA !== null
      ? formData.CELULAR_IMOBILIARIA
      : safeUserConfig.celular_imobiliaria || ""
  );
  data.EMAIL_IMOBILIARIA =
    formData.EMAIL_IMOBILIARIA !== undefined &&
    formData.EMAIL_IMOBILIARIA !== null
      ? formData.EMAIL_IMOBILIARIA
      : safeUserConfig.email_imobiliaria || "";
  data.CRECI_IMOBILIARIA =
    formData.CRECI_IMOBILIARIA !== undefined &&
    formData.CRECI_IMOBILIARIA !== null
      ? formData.CRECI_IMOBILIARIA
      : safeUserConfig.creci_imobiliaria || "";
  data.SITE_IMOBILIARIA =
    formData.SITE_IMOBILIARIA !== undefined &&
    formData.SITE_IMOBILIARIA !== null
      ? formData.SITE_IMOBILIARIA
      : safeUserConfig.site_imobiliaria || "";

  if (formData.proprietarios?.length) {
    data.PROPRIETARIOS = formData.proprietarios
      .map((p) => buildPessoaFisicaBlockHtml(p))
      .join("<br /><br />");

    data.ASSINATURA_PROPRIETARIOS = formData.proprietarios
      .map((p) =>
        signatureBlock(p.NOME_PESSOA_FISICA, formatCpfPreview(p.CPF_PESSOA_FISICA))
      )
      .join("\n\n");

    data.ASSINATURA_CONJUGE_PROPRIETARIOS = formData.proprietarios
      .filter((p) => p.ESTADO_CIVIL_PESSOA_FISICA === "casado")
      .map((p) =>
        signatureBlockConjugeHtml(p.NOME_CONJUGE, p.CPF_CONJUGE)
      )
      .join('<div class="h-4" aria-hidden="true"></div>');
  }

  data.INTERMEDIADOR = buildIntermediadorPreviewHtml(formData);
  data.ASSINATURA_INTERMEDIADOR = buildAssinaturaIntermediador(formData);

  if (formData.testemunhas?.length) {
    const lines = formData.testemunhas.map((t) =>
      signatureBlock(t.NOME_TESTEMUNHA, formatCpfPreview(t.CPF_TESTEMUNHA))
    );
    const joined = lines.join("\n\n");
    data.ASSINATURA_TESTEMUNHAS = joined;
    data.ASSINATURA_TESTEMUNHA = joined;
  }

  if (formData.EXCLUSIVIDADE != null && formData.EXCLUSIVIDADE !== "") {
    const k = String(formData.EXCLUSIVIDADE).toLowerCase();
    data.EXCLUSIVIDADE = EXCLUSIVIDADE_TEXTO[k] ?? formData.EXCLUSIVIDADE;
  }

  if (formData.PRAZO_AUTORIZACAO != null && formData.PRAZO_AUTORIZACAO !== "") {
    const n = Number(formData.PRAZO_AUTORIZACAO);
    data.PRAZO_AUTORIZACAO = Number.isFinite(n)
      ? String(Math.trunc(n))
      : String(formData.PRAZO_AUTORIZACAO);
  }

  if (formData.PERCENTUAL_COMISSAO != null) {
    data.PERCENTUAL_COMISSAO = formatDecimalBrForDoc(
      formData.PERCENTUAL_COMISSAO
    );
  }
  if (formData.PERCENTUAL_COMISSAO_EXTENSO != null) {
    data.PERCENTUAL_COMISSAO_EXTENSO = String(formData.PERCENTUAL_COMISSAO_EXTENSO);
  }

  if (formData.VALOR_IMOVEL != null && formData.VALOR_IMOVEL !== "") {
    data.VALOR_IMOVEL = formatBrlCurrencyForDoc(formData.VALOR_IMOVEL);
  }
  if (formData.VALOR_IMOVEL_EXTENSO != null) {
    data.VALOR_IMOVEL_EXTENSO = String(formData.VALOR_IMOVEL_EXTENSO);
  }

  if (data.DATA_ASSINATURA) {
    data.DATA_ASSINATURA = formatDateBR(data.DATA_ASSINATURA);
  }

  return data;
}
